<template>
  <div class="row">
    <div class="col-md-12">
      <!-- <card type="plain" :title="$t('sidebar.generalAnalytics')"> -->
        <div id="stockMap" class="map map-big"></div>
      <!-- </card> -->
    </div>
  </div>
</template>

<script>
import api from "@/services/api";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
// import Card from '@/components/Card.vue';

// Import leaflet marker icons for Vite/Webpack 5 compatibility
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const customColors = {
  high: '#6da36d',
  medium: '#d1a666',
  low: '#ba5f5f',
};

// Fix for default marker icons in Webpack 5 / Vite
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow
});

export default {
  name: 'LeafletMaps',
  components: {
    // Card
  },
  data() {
    return {
      locations: [],
      map: null
    };
  },
  async created() {
    try {
      await this.fetchLocationData();
    } catch (error) {
      console.error('Error fetching location data:', error); // eslint-disable-line no-console
      this.$toast.error(this.$t('common.unableToRetrieveData'), { timeout: 0 });
    }
  },
  beforeUnmount() {
    // Clean up map instance when component is destroyed
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    async fetchLocationData() {
      // TODO: maxing out at 30 locations currently - lets figure out a better way than this magic number
      //       Maybe only show top 5 locations with most tools? 30 is too much. Should we only pick the thickest cluster?
      const locationWithToolInfoUrl = '/Location?includeToolInfo=true&currentPage=1&pageSize=30';
      try {
        const response = await api.get(locationWithToolInfoUrl);
        const data = response.data;
        if (data && data.result) {
          this.locations = this.processLocationData(data.result);
          // Use nextTick to ensure DOM is ready before initializing map
          this.$nextTick(() => {
            this.initStockMap();
          });
        } else {
          console.error('Unexpected data format from API', data); // eslint-disable-line no-console
        }
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },
    processLocationData(locations) {
      return locations
        .filter(loc => loc.latitude !== null && loc.longitude !== null)
        .map(loc => ({
          coords: [loc.latitude, loc.longitude],
          stock: loc.toolsFreeAtLocation,
          total: loc.toolsBelongingToLocation,
          inUse: loc.toolsInUseAtLocation,
          broken: loc.toolsBrokenAtLocation,
          outOfUse: loc.toolsOutOfUseAtLocation,
          loan: loc.toolsLoanedAtLocation,
          rent: loc.toolsRentedAtLocation,
          label: `${loc.toolsFreeAtLocation}/${loc.toolsBelongingToLocation}`,
          address: loc.address,
          locationType: loc.locationType?.type || ''
        }));
    },
    getPopupContent(loc) {
      return `<b>${loc.address}</b><br>
        ${this.$t('toolStatus.all')}: ${loc.total}<br>
        ${this.$t('toolStatus.free')}: ${loc.stock}<br>
        ${this.$t('toolStatus.inUse')}: ${loc.inUse}<br>
        ${this.$t('toolStatus.broken')}: ${loc.broken}<br>
        ${this.$t('toolStatus.outOfUse')}: ${loc.outOfUse}<br>
        ${this.$t('toolStatus.loaned')}: ${loc.loan}<br>
        ${this.$t('toolStatus.rented')}: ${loc.rent}`;
    },
    initStockMap() {
      const mapElement = document.getElementById('stockMap');
      if (!mapElement) {
        console.warn('Map element not found'); // eslint-disable-line no-console
        return;
      }

      if (!this.map) {
        this.map = L.map('stockMap');

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
        }).addTo(this.map);

        if (this.locations.length === 1) {
          // If there's only one location, set the view to this location with a fixed zoom level
          this.map.setView(this.locations[0].coords, 14);
        } else if (this.locations.length > 1) {
          // If there are multiple locations, fit the map to their bounds
          const bounds = new L.LatLngBounds(this.locations.map(loc => loc.coords));
          if (bounds.isValid()) {
            this.map.fitBounds(bounds);
          } else {
            console.warn('No valid locations to fit bounds'); // eslint-disable-line no-console
            this.map.setView([65.6835, -18.1009], 13); // Default view
          }
        } else {
          // Handle the case where there are no locations
          this.map.setView([65.6835, -18.1009], 13); // Default to some coordinates with a default zoom
        }
      }

      this.locations.forEach(loc => {
        const ratio = loc.total > 0 ? loc.stock / loc.total : 0;
        const color = ratio > 0.9 ? customColors.high : (ratio >= 0.5 ? customColors.medium : customColors.low);

        L.circle(loc.coords, {
          color: color,
          radius: 300
        }).addTo(this.map);

        L.marker(loc.coords, { icon: this.createNumberIcon(loc.label) })
          .addTo(this.map)
          .bindPopup(this.getPopupContent(loc));
      });
    },
    createNumberIcon(numberText) {
      return L.divIcon({
        className: 'custom-div-icon',
        html: `<div class="map-marker-label">${numberText}</div>`,
        iconSize: [40, 40],
        iconAnchor: [20, 20]
      });
    }
  }
};
</script>

<style lang="scss">
.card-map {
  .map {
    height: 300px;
    width: 100%;
  }
}

.map-big {
  height: 460px;
  width: 100%;
}

.custom-div-icon {
  text-align: center;
}

.map-marker-label {
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  color: black;
  background-color: rgba(255, 255, 255, 0.8);
}
</style>
